// API client for the Church Health Assessment module. Response shapes mirror
// packages/cca-parishes-worker/src/health-assessment exactly: keep the two in
// step. Public routes are token-gated (no auth); portal routes ride the
// parishes session cookie, with X-Requested-With on every mutating request
// (CSRF defense-in-depth, same convention as ../../api-client).

import { ApiError } from '../../api-client';

// ── Error type ───────────────────────────────────────────────────────────────

/** Carries the worker's machine-readable error code (e.g. 'closed',
 *  'not_found', 'below_min_n') alongside the HTTP status. When the worker
 *  also sent its own plain sentence (`message`, e.g. the launch wizard's 409
 *  facilitator_ambiguous), it rides ApiError's `serverMessage` like the rest
 *  of the portal, for a page that wants to show it (cca-5myzh). */
export class HealthApiError extends ApiError {
  constructor(
    message: string,
    status: number,
    public code: string | null,
    public body: Record<string, unknown>,
  ) {
    super(message, status, typeof body.message === 'string' ? body.message : undefined);
    this.name = 'HealthApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const init: RequestInit = { method, credentials: 'include' };
  if (method !== 'GET') {
    init.headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
    init.body = JSON.stringify(body ?? {});
  }
  if (signal) init.signal = signal;
  const res = await fetch(path, init);
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const code = typeof json.error === 'string' ? json.error : null;
    // The public routes carry the invitation token in the query string, so
    // error text (which flows into telemetry) must only ever name the bare
    // path, never the query.
    const bare = path.replace(/\?.*$/, '');
    throw new HealthApiError(
      `${method} ${bare} failed: HTTP ${res.status}${code ? ` (${code})` : ''}`,
      res.status,
      code,
      json,
    );
  }
  return json as T;
}

// ── Instrument (shared with the worker via @cca/parishes-shared) ─────────────

export type { InstrumentItem, InstrumentSection, InstrumentDefinition } from '@cca/parishes-shared';
import type { InstrumentDefinition } from '@cca/parishes-shared';

export interface InstrumentVersionInfo {
  version: string;
  published_at: string;
  cycle_count: number;
  is_default: boolean;
}

/** One named instrument: mutable catalogue name, immutable version history. */
export interface InstrumentFamily {
  id: string;
  name: string;
  created_at: string;
  suggested_next: string;
  versions: InstrumentVersionInfo[];
}

export interface InstrumentVersionsResponse {
  families: InstrumentFamily[];
  default_version: string;
  default_family_id: string;
  suggested_next_new_family: string;
}

export interface CreateCycleBody {
  church_zoho_id: string;
  facilitator_email?: string;
  facilitator_name?: string;
  /** Create the CRM contact / grant the Facilitator role when missing. */
  ensure_facilitator?: boolean;
  opens_on?: string;
  closes_on?: string;
  mode?: CycleMode;
  instrument_version?: string;
  auto_reminders?: 'off' | 'on';
  edit_reauth?: 'off' | 'on';
  /** Resubmit despite an existing open cycle for the same church. */
  force?: boolean;
}

/** Why a facilitator fix-up the admin ticked was not applied:
 *  'lookup_failed' — the launch-time CRM lookup did not answer, so the server
 *  skipped the fix-up rather than risk a second Contact; 'fixup_failed' — the
 *  CRM write itself failed. */
export type FacilitatorFixupSkipped = 'lookup_failed' | 'fixup_failed';

export interface CreateCycleResponse {
  ok: boolean;
  cycle_id: string;
  zoho_cycle_id: string | null;
  zoho_synced: boolean;
  facilitator_matched: boolean | null;
  facilitator_fixed: 'created' | 'granted' | null;
  /** Set only when the admin ticked the facilitator fix-up and it was not
   *  applied; null when none was asked for, or it was applied. The assessment
   *  launched either way; step 2 says which fix-up was dropped and where to
   *  act (cca-5myzh, LeC S2, Miranda S-A). */
  facilitator_fixup_skipped: FacilitatorFixupSkipped | null;
  instrument_version: string;
}

export interface PublishInstrumentResponse {
  ok: boolean;
  version: string;
  warnings: string[];
  is_default: boolean;
  family_id: string;
  family_name: string;
}

/** Publish target: an existing instrument, or a brand-new named one. */
export type PublishTarget = { familyId: string } | { newFamilyName: string };

// ── Public survey (worker: routes/public.ts) ────────────────────────────────

export type CycleStatus = 'open' | 'closed' | 'debriefed' | 'archived';
export type CycleMode = 'rostered' | 'open';

export interface PublicInstrumentResponse {
  /** The respondent's personal token. May differ from the URL token when an
   *  open link just minted a personal invitation. */
  token: string;
  cycle: {
    church_name: string;
    status: CycleStatus;
    opens_at: string | null;
    closes_at: string | null;
    mode: CycleMode;
    is_open: boolean;
    /** Status is open but the NZ opening date has not arrived yet. */
    not_yet_open: boolean;
  };
  invitation: {
    status: string;
    role: string | null;
    display_name: string | null;
    has_email: boolean;
    first_visit: boolean;
    submitted: boolean;
    /** A one-time code must be verified before this submitted response can be
     *  edited (cycle requires it, an email is on file, not yet unlocked). */
    edit_reauth_required: boolean;
  };
  roles: string[];
  instrument: InstrumentDefinition;
  answers: { scores: Record<string, number>; texts: Record<string, string> };
  /** Public Turnstile site key, present only when bot protection is on; the
   *  survey renders the widget and returns a token with the submission. */
  turnstile_site_key: string | null;
}

export interface SurveyAnswersBody {
  scores: Record<string, number>;
  texts: Record<string, string>;
  role?: string;
  turnstile_token?: string;
}

/** Draft payloads may carry explicit nulls: the worker's draft merge treats a
 *  null as "delete this answer", which is how a cleared score or an emptied
 *  reflection is prevented from resurrecting on the next merge. Submit
 *  payloads never carry nulls; cleared answers are omitted entirely. */
export interface DraftAnswersBody {
  scores: Record<string, number | null>;
  texts: Record<string, string | null>;
  role?: string;
}

// ── Portal (worker: routes/portal.ts) ───────────────────────────────────────

export type HealthPortalRole = 'admin' | 'facilitator' | 'church_leader';

export interface CycleSummary {
  id: string;
  church_name: string;
  status: CycleStatus;
  mode: CycleMode;
  opens_at: string | null;
  closes_at: string | null;
  facilitator_name: string | null;
  invited: number;
  submitted: number;
  created_at: string;
}

export interface CyclesResponse {
  role: HealthPortalRole;
  cycles: CycleSummary[];
}

export interface CycleDetail {
  cycle: {
    id: string;
    church_name: string;
    facilitator_name: string | null;
    status: CycleStatus;
    mode: CycleMode;
    auto_reminders: 'off' | 'on';
    edit_reauth: 'off' | 'on';
    opens_at: string | null;
    closes_at: string | null;
    instrument_version: string;
    open_link: string | null;
    bcc_emails?: string | null;
  };
  participation: {
    invited: number;
    submitted: number;
    opened: number;
    declined: number;
    bounced: number;
    min_n: number;
    min_n_text: number;
    analysis_unlocked: boolean;
  };
  is_admin: boolean;
  /** True for the assigned facilitator and admins; false for church leaders,
   *  who follow participation read-only. */
  can_manage: boolean;
}

export interface InvitationSummary {
  id: string;
  display_name: string | null;
  email: string | null;
  role: string | null;
  status: string;
  invited_at: string | null;
  last_email_at: string | null;
  reminder_count: number;
  delivery_state: string | null;
  first_opened_at: string | null;
  submitted_at: string | null;
}

export interface InviteSuggestion {
  name: string;
  email: string | null;
  zoho_contact_id: string;
  /** Respondent role pre-filled from the CRM record (clergy, warden,
   *  parish_council, ministry_leader, staff, other). Facilitator can change. */
  suggested_role: string;
}

// ── Quantitative analysis (worker: analysis.ts) ─────────────────────────────

export type HealthBand = 'embedded' | 'strong' | 'developing' | 'priority';

export interface ItemStat {
  item_id: string;
  section_no: number;
  text: string;
  group?: string;
  mean: number;
  count: number;
  respondents: number;
  distribution: { 1: number; 2: number; 3: number; 4: number };
  band: HealthBand;
  critical: boolean;
  criticalFlagged: boolean;
  governance: boolean;
  divergent: boolean;
  roleMeans?: Record<string, { mean: number; count: number }>;
}

export interface SectionStat {
  section_no: number;
  id: string;
  title: string;
  mean: number | null;
  band: HealthBand | null;
  itemCount: number;
  answeredItemCount: number;
}

export interface QuantAnalysis {
  suppressed: boolean;
  submitted: number;
  invited: number;
  completionPct: number | null;
  smallSample: boolean;
  sections: SectionStat[];
  items: ItemStat[];
  strengths: ItemStat[];
  priorities: ItemStat[];
  divergences: ItemStat[];
  criticalItems: ItemStat[];
  governanceItems: ItemStat[];
  roleParticipation?: Record<string, number>;
}

// ── Qualitative analysis (worker: ai.ts + routes/portal.ts) ─────────────────

export type ThemeSignal = 'strength' | 'watch' | 'risk' | 'mixed';

export interface SectionThemes {
  section_no: number;
  themes: Array<{ title: string; summary: string; signal: ThemeSignal; points: string[] }>;
  encouragements: string[];
  questions_for_debrief: string[];
}

export interface CycleAiSummary {
  narrative: string[];
  give_thanks: string[];
  attend_to: string[];
  divergences: string[];
  debrief_questions: string[];
}

export interface VerbatimSection {
  section_no: number;
  questions: Array<{ id: string; text: string; answers: string[] }>;
}

/** suppressed: below MIN_N_TEXT (counts only). awaiting_close: cycle still
 *  open, summaries run at close. pending: a pass is running; poll. no_text:
 *  nobody wrote anything. unavailable: summaries cannot run; verbatim only. */
export type QualStatus =
  'suppressed' | 'ready' | 'pending' | 'awaiting_close' | 'no_text' | 'unavailable';

export interface QualAnalysis {
  status: QualStatus;
  submitted: number;
  min_n_text: number;
  verbatim?: VerbatimSection[];
  themes?: SectionThemes[];
  summary?: CycleAiSummary | null;
  generated_at?: string | null;
}

// ── Admin (worker: routes/admin.ts, settings.ts) ────────────────────────────

export interface HealthSettings {
  module_enabled: boolean;
  sender: string;
  reply_to: string;
  min_n: number;
  min_n_text: number;
  min_n_role: number;
  critical_items: string[];
  governance_items: string[];
  divergence_min_n: number;
  default_cycle_mode: CycleMode;
  auto_reminders: 'off' | 'on';
  reminder_min_gap_days: number;
  reminder_max: number;
  email_max_attempts: number;
  response_text_retention_days: number;
  edit_reauth: 'off' | 'on';
  timezone: string;
  ai_model: string;
  /** Public Cloudflare Turnstile site key; empty when bot protection is off. */
  turnstile_site_key: string;
}

export interface EmailFailureRow {
  id: number;
  cycle_id: string | null;
  to_email: string;
  template: string;
  status: string;
  error: string | null;
  attempts: number;
  created_at: string;
}

export interface SuppressionRow {
  email: string;
  reason: string;
  created_at: string;
}

export interface AuditRow {
  actor: string;
  action: string;
  cycle_id: string | null;
  detail: string | null;
  at: string;
}

export interface OpsResponse {
  failures: EmailFailureRow[];
  suppression: SuppressionRow[];
  audit: AuditRow[];
}

/** A single de-anonymised roster row (admin break-glass; audit-logged). Maps an
 *  invited person to the fact and time of their submission, never their answers. */
export interface RawRevealRow {
  display_name: string | null;
  email: string | null;
  response_id: string;
  role: string;
  submitted_at: string;
  updated_at: string;
  revision: number;
}

export interface RawRevealResponse {
  caption: string;
  responses: RawRevealRow[];
}

/** An open-link (self-served) response with abuse signals, for the §18
 *  "purge suspicious open-link responses" review. No identity — there is none. */
export interface OpenLinkResponseRow {
  response_id: string;
  role: string;
  submitted_at: string;
  revision: number;
  answer_count: number;
  ua_fingerprint: string;
  ua_duplicate_count: number;
  sparse: boolean;
  duplicate: boolean;
  suspicious: boolean;
}

export interface OpenLinkResponsesResponse {
  mode: CycleMode;
  responses: OpenLinkResponseRow[];
}

export type SafeguardingSignal = 'concern' | 'watch' | 'ok' | 'pending';

/** One cycle in the registrar's cross-church overview. Aggregate only. */
export interface CrossCycleRow {
  id: string;
  church_name: string;
  status: CycleStatus;
  mode: CycleMode;
  opens_at: string | null;
  closes_at: string | null;
  instrument_version: string;
  invited: number;
  submitted: number;
  completion_pct: number | null;
  analysis_ready: boolean;
  overall_mean: number | null;
  overall_band: HealthBand | null;
  safeguarding: SafeguardingSignal;
}

export interface CrossResponse {
  summary: {
    cycles: number;
    churches: number;
    open: number;
    closed: number;
    safeguarding_concern: number;
    safeguarding_watch: number;
  };
  cycles: CrossCycleRow[];
}

export interface TrendSection {
  section_no: number;
  id: string;
  title: string;
  mean: number | null;
  band: HealthBand | null;
}

/** One assessment in a church's history (chronological). Aggregate only. */
export interface TrendPoint {
  cycle_id: string;
  status: CycleStatus;
  opens_at: string | null;
  closes_at: string | null;
  instrument_version: string;
  submitted: number;
  overall_mean: number | null;
  overall_band: HealthBand | null;
  safeguarding: SafeguardingSignal;
  sections: TrendSection[];
}

export interface TrendResponse {
  church_name: string;
  current_cycle_id: string;
  points: TrendPoint[];
}

export interface CycleSettingsPatch {
  mode?: CycleMode;
  auto_reminders?: 'off' | 'on';
  edit_reauth?: 'off' | 'on';
  opens_at?: string;
  closes_at?: string;
  bcc_emails?: string;
  status?: CycleStatus;
}

// ── Client ──────────────────────────────────────────────────────────────────

const q = encodeURIComponent;

export const healthApi = {
  // Public (token-gated; the worker treats these as CSRF-exempt, the header
  // the shared wrapper adds is harmless)
  publicInstrument: (token: string) =>
    request<PublicInstrumentResponse>('GET', `/api/health/public/instrument?token=${q(token)}`),
  publicDraft: (token: string, body: DraftAnswersBody, signal?: AbortSignal) =>
    request<{ ok: true }>('PUT', `/api/health/public/draft?token=${q(token)}`, body, signal),
  publicSubmit: (token: string, body: SurveyAnswersBody) =>
    request<{ ok: true; revision: number; closes_at: string | null }>(
      'POST',
      `/api/health/public/submit?token=${q(token)}`,
      body,
    ),
  publicDecline: (token: string) =>
    request<{ ok: true }>('POST', `/api/health/public/decline?token=${q(token)}`),
  requestEditPin: (token: string) =>
    request<{ ok: true }>('POST', `/api/health/public/edit/request-pin?token=${q(token)}`),
  verifyEditPin: (token: string, pin: string) =>
    request<{ ok: true }>('POST', `/api/health/public/edit/verify-pin?token=${q(token)}`, { pin }),

  // Portal (authed)
  cycles: () => request<CyclesResponse>('GET', '/api/health/cycles'),
  cycle: (id: string) => request<CycleDetail>('GET', `/api/health/cycle/${q(id)}`),
  cycleInstrument: (id: string) =>
    request<{ version: string; definition: InstrumentDefinition }>(
      'GET',
      `/api/health/cycle/${q(id)}/instrument`,
    ),
  invitations: (cycleId: string) =>
    request<{ invitations: InvitationSummary[] }>(
      'GET',
      `/api/health/cycle/${q(cycleId)}/invitations`,
    ),
  suggestions: (cycleId: string) =>
    request<{ suggestions: InviteSuggestion[] }>(
      'GET',
      `/api/health/cycle/${q(cycleId)}/suggestions`,
    ),
  addInvitees: (
    cycleId: string,
    invitees: Array<{ name?: string; email: string; role?: string }>,
  ) =>
    request<{ ok: true; added: number; skipped: number }>(
      'POST',
      `/api/health/cycle/${q(cycleId)}/invitations`,
      { invitees },
    ),
  sendPendingInvites: (cycleId: string, invitationIds?: string[]) =>
    request<{ ok: true; sent: number; failed: number; skipped_not_pending: number }>(
      'POST',
      `/api/health/cycle/${q(cycleId)}/invitations/send`,
      invitationIds ? { invitation_ids: invitationIds } : {},
    ),
  remindNonResponders: (cycleId: string) =>
    request<{ ok: true; sent: number; eligible: number }>(
      'POST',
      `/api/health/cycle/${q(cycleId)}/invitations/remind`,
    ),
  resendInvitation: (invId: string) =>
    request<{ ok: boolean }>('POST', `/api/health/invitation/${q(invId)}/resend`),
  remindInvitation: (invId: string) =>
    request<{ ok: boolean }>('POST', `/api/health/invitation/${q(invId)}/remind`),
  patchInvitation: (
    invId: string,
    body: { action: 'set_role' | 'decline' | 'remove'; role?: string },
  ) => request<{ ok: true }>('PATCH', `/api/health/invitation/${q(invId)}`, body),
  quant: (cycleId: string) =>
    request<QuantAnalysis>('GET', `/api/health/analysis/${q(cycleId)}/quant`),
  trend: (cycleId: string) =>
    request<TrendResponse>('GET', `/api/health/cycle/${q(cycleId)}/trend`),
  qual: (cycleId: string) =>
    request<QualAnalysis>('GET', `/api/health/analysis/${q(cycleId)}/qual`),
  regenerateQual: (cycleId: string) =>
    request<{ ok: true; status: string }>('POST', `/api/health/analysis/${q(cycleId)}/regenerate`),

  // Admin
  settings: () =>
    request<{ settings: HealthSettings; turnstile_secret_configured: boolean }>(
      'GET',
      '/api/health/admin/settings',
    ),
  patchSettings: (patch: Record<string, string>) =>
    request<{ ok: true; updated: string[]; rejected: string[] }>(
      'PATCH',
      '/api/health/admin/settings',
      patch,
    ),
  ops: () => request<OpsResponse>('GET', '/api/health/admin/ops'),
  cross: () => request<CrossResponse>('GET', '/api/health/admin/cross'),
  suppressionAdd: (email: string) =>
    request<{ ok: true }>('POST', '/api/health/admin/suppression', { email }),
  suppressionRemove: (email: string) =>
    request<{ ok: true }>('DELETE', '/api/health/admin/suppression', { email }),
  patchCycleSettings: (cycleId: string, body: CycleSettingsPatch) =>
    request<{ ok: true }>('PATCH', `/api/health/admin/cycle/${q(cycleId)}/settings`, body),
  purgeText: (cycleId: string) =>
    request<{ ok: true; purged: number }>(
      'POST',
      `/api/health/admin/cycle/${q(cycleId)}/purge-text`,
    ),
  rawReveal: (cycleId: string) =>
    request<RawRevealResponse>('GET', `/api/health/admin/raw/${q(cycleId)}`),
  openLinkResponses: (cycleId: string) =>
    request<OpenLinkResponsesResponse>(
      'GET',
      `/api/health/cycle/${q(cycleId)}/open-link-responses`,
    ),
  purgeOpenLink: (cycleId: string, responseIds: string[]) =>
    request<{ ok: true; purged: number }>(
      'POST',
      `/api/health/cycle/${q(cycleId)}/open-link-purge`,
      { response_ids: responseIds },
    ),

  // Instrument editor (admin)
  instrumentVersions: () =>
    request<InstrumentVersionsResponse>('GET', '/api/health/admin/instrument/versions'),
  instrument: (version: string) =>
    request<{
      version: string;
      published_at: string | null;
      family_id: string;
      family_name: string;
      definition: InstrumentDefinition;
    }>('GET', `/api/health/admin/instrument/${q(version)}`),
  publishInstrument: (
    definition: InstrumentDefinition,
    setDefault: boolean,
    target: PublishTarget,
  ) =>
    request<PublishInstrumentResponse>('POST', '/api/health/admin/instrument', {
      definition,
      set_default: setDefault,
      ...('familyId' in target
        ? { family_id: target.familyId }
        : { new_family_name: target.newFamilyName }),
    }),
  renameInstrumentFamily: (id: string, name: string) =>
    request<{ ok: true; id: string; name: string }>(
      'PATCH',
      `/api/health/admin/instrument/family/${q(id)}`,
      { name },
    ),
  /** Edit a published version IN PLACE, graduated by severity (wording/additive;
   *  destructive is refused server-side with 409 destructive_change). */
  editInstrumentVersion: (version: string, definition: InstrumentDefinition) =>
    request<{
      ok: boolean;
      version?: string;
      change_kind?: 'wording' | 'additive';
      affected_cycles?: number;
      unchanged?: boolean;
    }>('PATCH', `/api/health/admin/instrument/${q(version)}`, { definition }),
  /** Ingest a pasted document into a draft definition (never publishes). This
   *  is a long call — a big document takes a minute or two to structure. */
  ingestInstrument: (text: string) =>
    request<{ definition: InstrumentDefinition; model: string }>(
      'POST',
      '/api/health/admin/instrument/ingest',
      { text },
    ),
  // Launching an assessment from the portal (admin only)
  adminChurches: () =>
    request<{ churches: Array<{ id: string; name: string }> }>('GET', '/api/health/admin/churches'),
  /** A POST, not a GET: the mailbox rides the JSON body, never the URL, as
   *  Workers Logs keep a request line unredacted for 7 days (cca-5myzh, Lazo).
   *  `request` adds the CSRF header to it, as to every non-GET. */
  contactLookup: (email: string) =>
    request<{
      found: boolean;
      /** The mailbox is on two or more Contacts (or the search was cut short):
       *  reported as not found, and a launch naming it is refused whole (409
       *  facilitator_ambiguous, before any write), so the wizard says why
       *  instead of offering a fix-up (cca-5myzh). */
      ambiguous?: boolean;
      zoho_error?: boolean;
      name?: string | null;
      zoho_contact_id?: string;
      church_health_roles?: string[];
      is_facilitator?: boolean;
    }>('POST', '/api/health/admin/contact-lookup', { email }),
  createCycle: (body: CreateCycleBody) =>
    request<CreateCycleResponse>('POST', '/api/health/admin/cycle', body),
  // Cycle instrument selection (admin; portal decides, Zoho is mirrored)
  setCycleInstrument: (cycleId: string, version: string) =>
    request<{ ok: true; version: string; unchanged?: boolean; zoho_synced: boolean | null }>(
      'POST',
      `/api/health/admin/cycle/${q(cycleId)}/instrument`,
      { version },
    ),
};

// CSV export URLs (plain links, session cookie rides along)
export const exportUrls = {
  scores: (cycleId: string) => `/api/health/export/scores.csv?cycle=${q(cycleId)}`,
  qualitative: (cycleId: string) => `/api/health/export/qualitative.csv?cycle=${q(cycleId)}`,
  invites: (cycleId: string) => `/api/health/export/invites.csv?cycle=${q(cycleId)}`,
  summaryPdf: (cycleId: string) => `/api/health/export/summary.pdf?cycle=${q(cycleId)}`,
  reportPdf: (cycleId: string) => `/api/health/export/report.pdf?cycle=${q(cycleId)}`,
};
